const CHAT_DISABLED_AGENT_PROVIDERS = new Set(["OpenAI Translator", "OpenAI Transcription", "OpenAI Summary"]);
export const PARTICIPANT_SEARCH_RESULT_LIMIT = 100;
export const PARTICIPANT_SEARCH_DEBOUNCE_MS = 80;

export function shouldShowAgentChat({ textChatEnabled, provider }) {
  if (!textChatEnabled) {
    return false;
  }

  return !CHAT_DISABLED_AGENT_PROVIDERS.has(provider);
}

export function normalizeParticipantSearchQuery(searchQuery) {
  return (searchQuery || "").trim().toLowerCase();
}

export function createParticipantSearchResult(people, searchQuery, limit = PARTICIPANT_SEARCH_RESULT_LIMIT) {
  const normalizedSearchQuery = normalizeParticipantSearchQuery(searchQuery);

  if (normalizedSearchQuery === "") {
    return {
      people,
      searchActive: false,
      truncated: false
    };
  }

  const filteredPeople = [];
  let truncated = false;

  for (const person of people) {
    const displayName = (person?.profile?.displayName || "").toLowerCase();

    if (!displayName.includes(normalizedSearchQuery)) {
      continue;
    }

    if (filteredPeople.length >= limit) {
      truncated = true;
      break;
    }

    filteredPeople.push(person);
  }

  return {
    people: filteredPeople,
    searchActive: true,
    truncated
  };
}

export function createAgentSearchResult(agents, searchQuery, limit = PARTICIPANT_SEARCH_RESULT_LIMIT) {
  const normalizedSearchQuery = normalizeParticipantSearchQuery(searchQuery);

  if (normalizedSearchQuery === "") {
    return {
      agents,
      searchActive: false,
      truncated: false
    };
  }

  const filteredAgents = [];
  let truncated = false;

  for (const agent of agents) {
    const name = (agent?.name || "").toLowerCase();

    if (!name.includes(normalizedSearchQuery)) {
      continue;
    }

    if (filteredAgents.length >= limit) {
      truncated = true;
      break;
    }

    filteredAgents.push(agent);
  }

  return {
    agents: filteredAgents,
    searchActive: true,
    truncated
  };
}

export function createParticipantChatStatusByUserId(myMessages, mySessionId, people) {
  const onlineUserIds = new Set((people || []).map(person => person?.id).filter(Boolean));
  const statusByUserId = new Map();

  (myMessages || []).forEach(group => {
    let otherUserId = null;

    if (group?.senderSessionId === mySessionId) {
      otherUserId = group.receiver;
    } else if (group?.receiver === mySessionId) {
      otherUserId = group.senderSessionId;
    }

    if (!otherUserId || !onlineUserIds.has(otherUserId)) {
      return;
    }

    const previousStatus = statusByUserId.get(otherUserId) || {
      latestMessage: null,
      unreadMessagesCount: ""
    };
    const latestMessage =
      !previousStatus.latestMessage || group.timestamp > previousStatus.latestMessage.timestamp
        ? group
        : previousStatus.latestMessage;
    const unreadMessagesCount =
      group.receiver === mySessionId && group.senderSessionId === otherUserId
        ? (previousStatus.unreadMessagesCount || 0) +
          group.messages.reduce((total, message) => total + (message?.read ? 0 : 1), 0)
        : previousStatus.unreadMessagesCount;

    statusByUserId.set(otherUserId, {
      latestMessage,
      unreadMessagesCount: unreadMessagesCount || ""
    });
  });

  return statusByUserId;
}

function normalizeVirtualHeight(height) {
  return Number.isFinite(height) ? Math.max(0, height) : 0;
}

function findFirstItemEndingAfter(offsets, heights, scrollTop) {
  let low = 0;
  let high = offsets.length - 1;
  let result = offsets.length;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);

    if (offsets[middle] + heights[middle] > scrollTop) {
      result = middle;
      high = middle - 1;
    } else {
      low = middle + 1;
    }
  }

  return result;
}

function findFirstItemStartingAtOrAfter(offsets, scrollBottom) {
  let low = 0;
  let high = offsets.length - 1;
  let result = offsets.length;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);

    if (offsets[middle] >= scrollBottom) {
      result = middle;
      high = middle - 1;
    } else {
      low = middle + 1;
    }
  }

  return result;
}

export function getVirtualListWindow({ itemHeights, scrollTop = 0, viewportHeight = 0, overscan = 3 }) {
  if (!itemHeights?.length) {
    return {
      endIndex: 0,
      items: [],
      startIndex: 0,
      totalHeight: 0
    };
  }

  const heights = itemHeights.map(normalizeVirtualHeight);
  const offsets = [];
  let totalHeight = 0;

  heights.forEach(height => {
    offsets.push(totalHeight);
    totalHeight += height;
  });

  const safeScrollTop = Math.max(0, Number.isFinite(scrollTop) ? scrollTop : 0);
  const safeViewportHeight = Math.max(0, Number.isFinite(viewportHeight) ? viewportHeight : 0);
  const safeOverscan = Math.max(0, Number.isFinite(overscan) ? Math.floor(overscan) : 0);
  const visibleStartIndex = Math.min(findFirstItemEndingAfter(offsets, heights, safeScrollTop), heights.length - 1);
  const visibleEndIndex = Math.max(
    visibleStartIndex + 1,
    findFirstItemStartingAtOrAfter(offsets, safeScrollTop + safeViewportHeight)
  );
  const startIndex = Math.max(0, visibleStartIndex - safeOverscan);
  const endIndex = Math.min(heights.length, visibleEndIndex + safeOverscan);
  const items = [];

  for (let index = startIndex; index < endIndex; index++) {
    items.push({
      height: heights[index],
      index,
      offset: offsets[index]
    });
  }

  return {
    endIndex,
    items,
    startIndex,
    totalHeight
  };
}
